/**
 * OAUTH 2.1 OG AI-TILKOBLINGENE (B3, `docs/AI-ANALYSE.md` §4).
 *
 * TO SLAGS FORMER I SAMME FIL, OG DE FØLGER HVER SIN REGEL:
 *
 * * OAuth-endepunktene (`/.well-known/oauth-authorization-server`, `/v1/oauth/register`, `/token`,
 *   `/revoke`) snakker med AI-appenes servere og følger RFC-ene ordrett — `snake_case`, og feil som
 *   `{ error, error_description }`. Det er det eneste stedet i API-et der JSON ikke er camelCase og
 *   feilen ikke er den felles konvolutten: en klient som følger standarden, skal ikke kjenne Tvitvi.
 * * Godkjenningssiden, tilkoblingene og bryterne er Tvitvis egne og følger husets regler (camelCase,
 *   felles feilformat).
 *
 * Inndata til OAuth-endepunktene er med vilje løse (`looseObject`, alt valgfritt): en manglende eller
 * ugyldig verdi skal bli et OAuth-svar (`invalid_request`), ikke en 400 i husets format.
 */
import { z } from 'zod';

import { EXTERNAL_APPROVAL_GROUPS, EXTERNAL_BLOCKED_GROUPS } from '../policy/external-groups.js';

/**
 * Scopene serveren og MCP-serveren annonserer (B2 setter `MCP_SCOPES` til den samme lista). Tilgangen
 * avgjøres ikke av scopet, men av brukerens rettigheter, organisasjonens brytere og sperrelista; scopet
 * finnes fordi klienter ber om ett. Et ukjent scope i en forespørsel ignoreres.
 */
export const OAUTH_SCOPES = ['tvitvi'] as const;

/* ------------------------------------------------------------------ RFC 8414 */

export const oauthAuthorizationServerMetadataSchema = z
  .object({
    issuer: z.string(),
    authorization_endpoint: z.string(),
    token_endpoint: z.string(),
    registration_endpoint: z.string(),
    revocation_endpoint: z.string(),
    response_types_supported: z.array(z.string()),
    response_modes_supported: z.array(z.string()),
    grant_types_supported: z.array(z.string()),
    code_challenge_methods_supported: z.array(z.string()),
    token_endpoint_auth_methods_supported: z.array(z.string()),
    revocation_endpoint_auth_methods_supported: z.array(z.string()),
    scopes_supported: z.array(z.string()),
    client_id_metadata_document_supported: z.boolean(),
    authorization_response_iss_parameter_supported: z.boolean(),
    service_documentation: z.string().optional(),
  })
  .meta({ id: 'OAuthAuthorizationServerMetadata' });
export type OAuthAuthorizationServerMetadata = z.infer<
  typeof oauthAuthorizationServerMetadataSchema
>;

/** RFC 6749 §5.2 / RFC 7591 §3.2.2. */
export const oauthErrorSchema = z.object({
  error: z.string(),
  error_description: z.string().optional(),
});
export type OAuthError = z.infer<typeof oauthErrorSchema>;

/* ------------------------------------------------------------------ RFC 7591 (DCR) */

export const oauthClientRegistrationRequestSchema = z
  .looseObject({
    redirect_uris: z.unknown().optional(),
    client_name: z.unknown().optional(),
    token_endpoint_auth_method: z.unknown().optional(),
    grant_types: z.unknown().optional(),
    response_types: z.unknown().optional(),
    scope: z.unknown().optional(),
    application_type: z.unknown().optional(),
  })
  .meta({ id: 'OAuthClientRegistrationRequest' });
export type OAuthClientRegistrationRequest = z.infer<typeof oauthClientRegistrationRequestSchema>;

const registeredClientSchema = z.object({
  client_id: z.string(),
  client_id_issued_at: z.number().int(),
  client_name: z.string(),
  redirect_uris: z.array(z.string()),
  grant_types: z.array(z.string()),
  response_types: z.array(z.string()),
  token_endpoint_auth_method: z.string(),
  client_secret: z.string().optional(),
  client_secret_expires_at: z.number().int().optional(),
});

export const oauthClientRegistrationResponseSchema = z
  .union([registeredClientSchema, oauthErrorSchema])
  .meta({ id: 'OAuthClientRegistrationResponse' });
export type OAuthClientRegistrationResponse = z.infer<typeof oauthClientRegistrationResponseSchema>;
export type OAuthRegisteredClient = z.infer<typeof registeredClientSchema>;

/* ------------------------------------------------------------------ authorize */

export const oauthAuthorizeQuerySchema = z
  .looseObject({
    response_type: z.string().optional(),
    client_id: z.string().optional(),
    redirect_uri: z.string().optional(),
    state: z.string().optional(),
    scope: z.string().optional(),
    code_challenge: z.string().optional(),
    code_challenge_method: z.string().optional(),
    resource: z.unknown().optional(),
  })
  .meta({ id: 'OAuthAuthorizeQuery' });
export type OAuthAuthorizeQuery = z.infer<typeof oauthAuthorizeQuerySchema>;

/* ------------------------------------------------------------------ token og revoke */

export const oauthTokenRequestSchema = z
  .looseObject({
    grant_type: z.string().optional(),
    code: z.string().optional(),
    redirect_uri: z.string().optional(),
    code_verifier: z.string().optional(),
    client_id: z.string().optional(),
    client_secret: z.string().optional(),
    refresh_token: z.string().optional(),
    resource: z.unknown().optional(),
    scope: z.string().optional(),
  })
  .meta({ id: 'OAuthTokenRequest' });
export type OAuthTokenRequest = z.infer<typeof oauthTokenRequestSchema>;

const issuedTokensSchema = z.object({
  access_token: z.string(),
  token_type: z.literal('Bearer'),
  expires_in: z.number().int(),
  refresh_token: z.string(),
  scope: z.string().optional(),
});

export const oauthTokenResponseSchema = z
  .union([issuedTokensSchema, oauthErrorSchema])
  .meta({ id: 'OAuthTokenResponse' });
export type OAuthTokenResponse = z.infer<typeof oauthTokenResponseSchema>;
export type OAuthIssuedTokens = z.infer<typeof issuedTokensSchema>;

export const oauthRevokeRequestSchema = z
  .looseObject({
    token: z.string().optional(),
    token_type_hint: z.string().optional(),
    client_id: z.string().optional(),
    client_secret: z.string().optional(),
  })
  .meta({ id: 'OAuthRevokeRequest' });
export type OAuthRevokeRequest = z.infer<typeof oauthRevokeRequestSchema>;

/** RFC 7009: 200 med tom kropp, også for et ukjent token. Feil bare for en ugyldig klient. */
export const oauthRevokeResponseSchema = z
  .union([z.object({}), oauthErrorSchema])
  .meta({ id: 'OAuthRevokeResponse' });
export type OAuthRevokeResponse = z.infer<typeof oauthRevokeResponseSchema>;

/* ------------------------------------------------------------------ godkjenningssiden (Tvitvis egen form) */

export const OAUTH_REQUEST_STATUSES = ['pending', 'expired', 'decided'] as const;

/* ------------------------------------------------------------------ klientfamiliene (1335) */

/** Organisasjonen godkjenner AI-klienter per familie. Speilet i basen er `app.ai_client_family`. */
export const AI_CLIENT_FAMILIES = [
  'claude',
  'chatgpt',
  'gemini',
  'grok',
  'copilot',
  'local',
] as const;
export const aiClientFamilySchema = z.enum(AI_CLIENT_FAMILIES);
export type AiClientFamily = z.infer<typeof aiClientFamilySchema>;

const FAMILY_BY_CLIENT_NAME: Readonly<Record<string, AiClientFamily>> = {
  Claude: 'claude',
  ChatGPT: 'chatgpt',
  Gemini: 'gemini',
  'Gemini Enterprise': 'gemini',
  Grok: 'grok',
  'VS Code': 'copilot',
  'Microsoft 365 Copilot': 'copilot',
};

/**
 * Familien til et klientnavn fra hvitelista. En maskinlokal klient heter «<eget navn> (lokal)» og er
 * `local` uansett hva den kaller seg. Et ukjent navn har ingen familie og slipper aldri inn.
 */
export function aiClientFamily(clientName: string): AiClientFamily | null {
  if (clientName.endsWith(' (lokal)')) return 'local';
  return FAMILY_BY_CLIENT_NAME[clientName] ?? null;
}

export const AI_CONSENT_BLOCK_REASONS = ['ai_off', 'client_not_allowed'] as const;

export const oauthConsentOrganizationSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: z.string(),
  role: z.string(),
  /** Organisasjonens AI-tilgang. */
  aiAccess: z.boolean(),
  /** «Lese og skrive» når true, «bare lese» ellers. */
  aiWrite: z.boolean(),
  /** Hvorfor organisasjonen står gråtonet og ikke kan velges; null når den kan. */
  reason: z.enum(AI_CONSENT_BLOCK_REASONS).nullable(),
});

export const oauthConsentRequestSchema = z
  .object({
    id: z.uuid(),
    status: z.enum(OAUTH_REQUEST_STATUSES),
    client: z.object({
      /** Fra hvitelista, aldri fra klienten (en klient kan ikke kalle seg «Claude»). */
      name: z.string(),
      type: z.enum(['ai', 'integration']),
      /** Vertsnavnet i redirect-adressen. MCP-spesifikasjonen krever at siden viser det. */
      redirectHost: z.string(),
      /** Adressen er på brukerens egen maskin (127.0.0.1/localhost): siden bør si det tydelig. */
      local: z.boolean(),
    }),
    organizations: z.array(oauthConsentOrganizationSchema),
    /** Organisasjonene en eksisterende tilkobling til den samme klienten allerede har. */
    connectedOrganizationIds: z.array(z.uuid()),
    /** Nøkler for «hva den aldri kan» og «bare med godkjenning»: `ai.blocked.<key>`, `ai.approval.<key>`. */
    neverAllowed: z.array(z.enum(EXTERNAL_BLOCKED_GROUPS)),
    requiresApproval: z.array(z.enum(EXTERNAL_APPROVAL_GROUPS)),
    expiresAt: z.iso.datetime(),
  })
  .meta({ id: 'OAuthConsentRequest' });
export type OAuthConsentRequest = z.infer<typeof oauthConsentRequestSchema>;

export const approveOAuthRequestSchema = z
  .object({
    organizationIds: z.array(z.uuid()).min(1).max(50),
  })
  .meta({ id: 'ApproveOAuthRequest' });
export type ApproveOAuthRequest = z.infer<typeof approveOAuthRequestSchema>;

/** Nettsiden sender nettleseren hit: AI-appens adresse med `code`/`error`, `state` og `iss`. */
export const oauthRedirectResponseSchema = z
  .object({ redirectUrl: z.string() })
  .meta({ id: 'OAuthRedirectResponse' });
export type OAuthRedirectResponse = z.infer<typeof oauthRedirectResponseSchema>;

/* ------------------------------------------------------------------ tilkoblingene */

const connectionOrganizationSchema = z.object({ id: z.uuid(), name: z.string() });

export const aiConnectionSchema = z.object({
  id: z.uuid(),
  clientName: z.string(),
  clientType: z.enum(['ai', 'integration']),
  organizations: z.array(connectionOrganizationSchema),
  createdAt: z.iso.datetime(),
  lastUsedAt: z.iso.datetime().nullable(),
});
export type AiConnection = z.infer<typeof aiConnectionSchema>;

export const aiConnectionListResponseSchema = z
  .object({ items: z.array(aiConnectionSchema) })
  .meta({ id: 'AiConnectionListResponse' });
export type AiConnectionListResponse = z.infer<typeof aiConnectionListResponseSchema>;

export const aiOrganizationConnectionSchema = aiConnectionSchema.extend({
  user: z.object({ personId: z.uuid(), displayName: z.string().nullable() }),
});
export type AiOrganizationConnection = z.infer<typeof aiOrganizationConnectionSchema>;

export const aiOrganizationConnectionListResponseSchema = z
  .object({ items: z.array(aiOrganizationConnectionSchema) })
  .meta({ id: 'AiOrganizationConnectionListResponse' });
export type AiOrganizationConnectionListResponse = z.infer<
  typeof aiOrganizationConnectionListResponseSchema
>;

/* ------------------------------------------------------------------ bryterne (B4) */

const namedPersonSchema = z.object({ personId: z.uuid(), displayName: z.string().nullable() });

export const aiOrganizationSettingsSchema = z
  .object({
    aiAccess: z.boolean(),
    /** Alltid false når `aiAccess` er false. */
    aiWrite: z.boolean(),
    /** Klientfamiliene som slipper inn. En tilkobling fra en annen familie når ikke organisasjonen. */
    allowedClients: z.array(aiClientFamilySchema),
    /** Sist superadmin bekreftet at organisasjonen har vurdert AI-tilgangen (kreves for å slå den på). */
    acknowledgedAt: z.iso.datetime().nullable(),
    acknowledgedBy: z.object({ displayName: z.string().nullable() }).nullable(),
    /** Organisasjonens superadmins: de som kan slå AI på. Plattformens støttetilgang er ikke med. */
    superadmins: z.array(namedPersonSchema),
  })
  .meta({ id: 'AiOrganizationSettings' });
export type AiOrganizationSettings = z.infer<typeof aiOrganizationSettingsSchema>;

/** Å slå `aiAccess` på fra av krever `acknowledged: true` (AI_ACKNOWLEDGEMENT_REQUIRED ellers). */
export const updateAiOrganizationSettingsRequestSchema = z
  .object({
    aiAccess: z.boolean().optional(),
    aiWrite: z.boolean().optional(),
    allowedClients: z.array(aiClientFamilySchema).max(AI_CLIENT_FAMILIES.length).optional(),
    acknowledged: z.boolean().optional(),
  })
  .refine(
    (body) =>
      body.aiAccess !== undefined ||
      body.aiWrite !== undefined ||
      body.allowedClients !== undefined ||
      body.acknowledged !== undefined,
    { message: 'Send aiAccess, aiWrite, allowedClients or acknowledged.' },
  )
  .meta({ id: 'UpdateAiOrganizationSettingsRequest' });
export type UpdateAiOrganizationSettingsRequest = z.infer<
  typeof updateAiOrganizationSettingsRequestSchema
>;

export const aiOrganizationRevokeAllResponseSchema = z
  .object({ revoked: z.number().int() })
  .meta({ id: 'AiOrganizationRevokeAllResponse' });
export type AiOrganizationRevokeAllResponse = z.infer<typeof aiOrganizationRevokeAllResponseSchema>;

/* ------------------------------------------------------------------ Control Room */

export const platformAiConnectionsResponseSchema = z
  .object({
    items: z.array(
      z.object({
        organizationId: z.uuid(),
        organizationName: z.string(),
        connections: z.number().int(),
        users: z.number().int(),
        lastUsedAt: z.iso.datetime().nullable(),
      }),
    ),
  })
  .meta({ id: 'PlatformAiConnectionsResponse' });
export type PlatformAiConnectionsResponse = z.infer<typeof platformAiConnectionsResponseSchema>;

export const platformAiRevokeAllResponseSchema = z
  .object({ revoked: z.number().int() })
  .meta({ id: 'PlatformAiRevokeAllResponse' });
export type PlatformAiRevokeAllResponse = z.infer<typeof platformAiRevokeAllResponseSchema>;
